import React, { useState, useEffect } from 'react';
import ExtractInfoModal from './ExtractInfoModal';
import { getExtractionResults, clearExtractionResults } from '../../../../../utils/aiExtraction';
import api from '../../../../../services/api';

/**
 * Component that monitors for AI extraction results in localStorage
 * and displays them in a modal when available
 */
const AIExtractionMonitor = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Check for extraction results on mount and when localStorage changes
  useEffect(() => {
    // Clear extraction results when the page loads/reloads
    // This prevents errors when the page reloads with extraction data still in localStorage
    console.log('[AI-Monitor] Page loaded - clearing any existing extraction results');
    clearExtractionResults();
    setExtractedData(null);
    setLeadData(null);
    setLastProcessedTimestamp(null);
    
    const checkForResults = () => {
      const results = getExtractionResults();
      if (results && results.data && results.leadId) {
        console.log('[AI-Monitor] Found extraction results:', results);
        
        // Validate data before setting it to state
        if (validateExtractionData(results.data)) {
          setExtractedData(results.data);
          fetchLeadData(results.leadId);
        } else {
          console.error('[AI-Monitor] Invalid extraction data, clearing results');
          clearExtractionResults();
        }
      }
    };

    // Check immediately
    checkForResults();

    // Also listen for storage events (though this won't catch programmatic changes in the same window)
    const handleStorageChange = (e) => {
      if (e.key === 'crm_ai_extraction_results') {
        checkForResults();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Keep track of the last processed extraction timestamp to avoid processing the same extraction twice
  const [lastProcessedTimestamp, setLastProcessedTimestamp] = useState(null);
  
  // Validate extraction data before using it
  const validateExtractionData = (data) => {
    if (!data || typeof data !== 'object') return false;
    
    try {
      // Check for any nested objects that might cause rendering issues
      const safeData = JSON.parse(JSON.stringify(data));
      return true;
    } catch (e) {
      console.error('[AI-Monitor] Data validation failed:', e);
      return false;
    }
  };
  
  // Poll for extraction results
  useEffect(() => {
    // Only start polling when no modal is currently open
    if (isModalOpen) return;
    
    console.log('[AI-Monitor] Starting polling for extraction results');
    
    const intervalId = setInterval(() => {
      const results = getExtractionResults();
      
      // Skip if no results or already processing a modal
      if (!results || !results.data || !results.leadId || isModalOpen) return;
      
      // Check if this is a new extraction (different timestamp)
      if (!lastProcessedTimestamp || results.timestamp !== lastProcessedTimestamp) {
        console.log('[AI-Monitor] Found new extraction results from polling:', results);
        
        // Validate data before setting it to state
        if (validateExtractionData(results.data)) {
          setExtractedData(results.data);
          setLastProcessedTimestamp(results.timestamp);
          fetchLeadData(results.leadId);
        } else {
          console.error('[AI-Monitor] Invalid extraction data from polling, clearing results');
          clearExtractionResults();
          setLastProcessedTimestamp(null);
        }
      }
    }, 1000); // Check every second

    // Clean up interval on unmount or when modal opens
    return () => {
      console.log('[AI-Monitor] Clearing polling interval');
      clearInterval(intervalId);
    };
  }, [isModalOpen, lastProcessedTimestamp]);

  // Sanitize problematic object properties to prevent rendering errors
  const sanitizeLeadData = (data) => {
    if (!data) return null;
    
    try {
      // Create a deep copy to avoid modifying the original
      const sanitized = JSON.parse(JSON.stringify(data));
      
      // Handle known problematic fields by converting objects to their string representation
      if (sanitized.stage && typeof sanitized.stage === 'object') {
        sanitized.stage = sanitized.stage.name || JSON.stringify(sanitized.stage);
      }
      
      if (sanitized.leadSource && typeof sanitized.leadSource === 'object') {
        sanitized.leadSource = sanitized.leadSource.name || JSON.stringify(sanitized.leadSource);
      }
      
      // Check for any other nested objects that might have nested _id, name fields
      Object.keys(sanitized).forEach(key => {
        const value = sanitized[key];
        if (value && typeof value === 'object' && !Array.isArray(value) && value._id && value.name) {
          console.log(`[AI-Monitor] Sanitizing complex object in field: ${key}`); 
          sanitized[key] = value.name;
        }
      });
      
      return sanitized;
    } catch (e) {
      console.error('[AI-Monitor] Error sanitizing data:', e);
      return null;
    }
  };

  // Fetch the lead data when extraction results are found
  const fetchLeadData = async (leadId) => {
    if (!leadId) return;
    
    try {
      setIsLoading(true);
      console.log(`[AI-Monitor] Fetching lead data for ID: ${leadId}`);
      
      const response = await api.get(`/api/leads/${leadId}`);
      if (response.data) {
        // Sanitize the lead data before setting it to state
        const sanitizedData = sanitizeLeadData(response.data);
        
        if (sanitizedData) {
          // First set the sanitized lead data
          setLeadData(sanitizedData);
          console.log(`[AI-Monitor] Got lead data for: ${sanitizedData.name || leadId}`);
          
          // Then open the modal after a slight delay to ensure state is updated
          setTimeout(() => {
            setIsModalOpen(true);
            console.log('[AI-Monitor] Opening extraction modal');
          }, 50);
        } else {
          console.error(`[AI-Monitor] Failed to sanitize lead data for ID: ${leadId}`);
          clearExtractionResults();
        }
      } else {
        console.warn(`[AI-Monitor] No lead data found for ID: ${leadId}`);
      }
    } catch (error) {
      console.error('[AI-Monitor] Error fetching lead data:', error);
      // Reset the processed timestamp so we can try again
      setLastProcessedTimestamp(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle closing the modal
  const handleClose = () => {
    console.log('[AI-Monitor] Closing extraction modal and resetting state');
    setIsModalOpen(false);
    setExtractedData(null);
    setLeadData(null);
    
    // Clear stored results
    clearExtractionResults();
    
    // Reset the processing timestamp after a short delay
    // This ensures we can process a new extraction right away
    setTimeout(() => {
      setLastProcessedTimestamp(null);
      console.log('[AI-Monitor] Reset timestamp - ready for next extraction');
    }, 100);
  };

  // Handle updating the lead with extracted data
  const handleUpdateLead = async (updateData) => {
    if (!leadData || !updateData) return;
    
    try {
      setIsLoading(true);
      const leadId = leadData.leadId || leadData._id;
      const response = await api.put(`/api/leads/${leadId}`, updateData);
      
      if (response.data) {
        console.log('[AI-Monitor] Lead updated with extracted data:', response.data);
        
        // Update the lead data in state
        setLeadData(response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('[AI-Monitor] Error updating lead with extracted data:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Function to validate that data can be safely rendered
  const isSafeToRender = () => {
    if (!leadData || !extractedData || !isModalOpen) return false;
    
    try {
      // Ensure we can safely stringify the objects (no circular references)
      JSON.stringify(leadData);
      JSON.stringify(extractedData);
      return true;
    } catch (e) {
      console.error('[AI-Monitor] Data not safe to render:', e);
      // Clean up the problematic data
      setTimeout(() => {
        clearExtractionResults();
        setExtractedData(null);
        setLeadData(null);
        setIsModalOpen(false);
      }, 0);
      return false;
    }
  };

  return (
    <>
      {isSafeToRender() && (
        <ExtractInfoModal
          isOpen={isModalOpen}
          onClose={handleClose}
          leadData={leadData}
          extractedData={extractedData}
          onUpdateLead={handleUpdateLead}
        />
      )}
    </>
  );
};

export default AIExtractionMonitor;
